import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Button,
    TextField,
    Container,
    Typography,
    Box,
    Alert,
    Paper,
    IconButton,
    InputAdornment,
    CircularProgress,
    useTheme
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { cores } from '../../theme';
import logoMedicina from '../../assets/logo-medicina.png';


const LoginPagina = () => {
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [erro, setErro] = useState('');
    const [carregando, setCarregando] = useState(false);
    const [mostrarSenha, setMostrarSenha] = useState(false);
    const navegar = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        // Remove margin e padding do body/html para eliminar bordas brancas
        const originalBodyStyle = document.body.style.cssText;
        const originalHtmlStyle = document.documentElement.style.cssText;

        document.body.style.margin = '0';
        document.body.style.padding = '0';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.margin = '0';
        document.documentElement.style.padding = '0';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.cssText = originalBodyStyle;
            document.documentElement.style.cssText = originalHtmlStyle;
        };
    }, []); const lidarComSubmissao = async (e) => {
        e.preventDefault();
        setErro('');
        setCarregando(true);
        try {
            await signInWithEmailAndPassword(auth, email, senha);
            navegar('/');
        } catch (err) {
            setErro('Falha ao fazer login. Verifique seu e-mail e senha.');
        } finally {
            setCarregando(false);
        }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                minHeight: '100vh',
                height: '100vh',
                width: '100vw',
                background: cores.gradientes.primario,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="xs" disableGutters sx={{ px: 1, maxWidth: { xs: '95%', sm: '400px' } }}>
                <Paper
                    elevation={8}
                    sx={{
                        p: { xs: 2, sm: 4 },
                        borderRadius: 3,
                        background: cores.fundo.papel,
                        boxShadow: cores.sombras.elevada,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxWidth: '100%',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: 80, sm: 100 },
                            height: { xs: 80, sm: 100 },
                            background: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: cores.sombras.padrao,
                            mb: 2,
                            p: 1,
                        }}
                    >
                        <img
                            src={logoMedicina}
                            alt="Logo Medicina"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                    <Typography component="h1" variant="h5" sx={{ fontWeight: 700, color: cores.primaria.principal, mb: 1, textAlign: 'center' }}>
                        RealMed
                    </Typography>
                    <Typography variant="body2" sx={{ color: cores.texto.secundario, mb: 3, textAlign: 'center' }}>
                        Entre na sua conta para continuar
                    </Typography>
                    <Box component="form" onSubmit={lidarComSubmissao} sx={{ width: '100%' }}>
                        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Endereço de E-mail"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={carregando}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: cores.fundo.claro,
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: cores.primaria.principal,
                                },
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="senha"
                            label="Senha"
                            type={mostrarSenha ? 'text' : 'password'}
                            id="senha"
                            autoComplete="current-password"
                            value={senha}
                            onChange={(e) => setSenha(e.target.value)}
                            disabled={carregando}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: cores.fundo.claro,
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: cores.primaria.principal,
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="alternar visibilidade da senha"
                                            onClick={() => setMostrarSenha(!mostrarSenha)}
                                            edge="end"
                                            disabled={carregando}
                                            size="small"
                                        >
                                            {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={carregando || !email || !senha}
                            sx={{
                                mt: 3,
                                mb: 2,
                                height: 48,
                                borderRadius: 2,
                                background: cores.gradientes.primario,
                                fontWeight: 'bold',
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: cores.sombras.botoes,
                                '&:hover': {
                                    background: cores.gradientes.secundario,
                                },
                                '&:disabled': {
                                    background: '#e2e8f0',
                                    color: cores.texto.desabilitado,
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {carregando ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Entrar'}
                        </Button>
                        <Typography variant="body2" align="center" sx={{ color: cores.texto.terciario, mt: 2 }}>
                            Não tem uma conta?{' '}
                            <RouterLink to="/cadastro" style={{ color: cores.primaria.principal, fontWeight: 600, textDecoration: 'none' }}>
                                Cadastre-se
                            </RouterLink>
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default LoginPagina;